// Theme: dark by default, light on request; kept on this device. tokens.css switches every colour on
// <html data-theme>; this module sets the attribute and <meta name="theme-color"> (the installed app's title bar).
import { useSyncExternalStore } from 'react'

export type Theme = 'dark' | 'light'
const KEY = 'nw:theme'

function stored(): Theme {
  try {
    return localStorage.getItem(KEY) === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark'
  }
}

let current: Theme = stored()
const listeners = new Set<() => void>()

function cssColour(name: string): string {
  const probe = document.createElement('canvas').getContext('2d')
  if (!probe) return ''
  probe.fillStyle = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  probe.fillRect(0, 0, 1, 1)
  const [r = 0, g = 0, b = 0] = probe.getImageData(0, 0, 1, 1).data
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
}

/** Applies a theme to the document; call once at startup and on every change. */
export function applyTheme(theme: Theme = current): void {
  document.documentElement.dataset.theme = theme
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  const colour = cssColour('--color-canvas')
  if (colour) meta.content = colour
}

export function setTheme(theme: Theme): void {
  current = theme
  try {
    localStorage.setItem(KEY, theme)
  } catch {
    /* storage unavailable */
  }
  applyTheme(theme)
  listeners.forEach((l) => l())
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const theme = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => current,
  )
  return [theme, setTheme]
}
